'use strict';
const { getDependencyClass } = require('../../lib/dependencies.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');
let measure = performanceMeasure('webcomponents:picture');

getDependencyClass('phoenix-page').then((pageClass) => {
    /**
     * PhoenixPicture
     *
     * A webcomponent for Phoenix picture elements that are to be lazily loaded
     * for optimal performance. This component will attach to our intersection
     * observer and on triggering will adjust the wrapped picture element,
     * changing the inner "source" tags by adjusting their "data-srcset" attribute
     * to be a real "srcset" attribute. The "img" tag within the picture
     * element still supplies a "src" attribute (which refers to the "share"
     * rendition of the image, suitable for SEO and image indexing), but also
     * supplies a "srcset" attribute which causes the web browser to ignore
     * it, showing the 1px GIF placeholder instead. The "is-waiting-to-load"
     * class on the picture element will be replaced with an "is-loaded" class
     * once the img element has loaded the real image.
     *
     * @example
     *     <figure>
     *       <phoenix-picture>
     *         <picture class="is-waiting-to-load">
     *           <source
     *             media="..."
     *             sizes="..."
     *             data-srcset="...">
     *           <source
     *             media="..."
     *             sizes="..."
     *             data-srcset="...">
     *           <img
     *              src="https://<canonical one for bots and old browsers>"
     *              srcset="<one pixel modern browsers and lazy-loading>"
     *              alt="<seo keyword>"
     *              width="16"
     *              height="9">
     *         </picture>
     *         <div class="placeholder">...</div>
     *       </phoenix-picture>
     *       <figcaption>...</figcaption>
     *     </figure>
     */
    class PhoenixPicture extends pageClass.PhoenixElement {
        connectedCallback() {
            this.imageSet = this.getAttr('image-set');

            if (this.imageSet) {
                this.imageSetCss = JSON.parse(this.imageSet)
                    .map(({ url, ratio, type }) => `url("${url}") ${ratio} type("image/${type}")`)
                    .join(', ');
                this.observeFirstIntersection();
            } else {
                // make sure the subtree is ready
                this.waitForElement('img', { untilDomReady: true }).then((img) => {
                    // If the loading attribute is supported and present
                    // on the img tag, then trigger the "intersect" behavior
                    // right away to hand over lazy loading to the browser.
                    // Note that the CSS class manipulation will still wait
                    // on the actual 'load' event for the img tag.
                    if ('loading' in HTMLImageElement.prototype && img.getAttribute('loading') === 'lazy') {
                        this.onFirstIntersection();
                    } else {
                        // start observing
                        this.observeFirstIntersection();
                    }
                });
            }
        }

        _swapLoadingClasses(elem) {
            elem.classList.remove('is-waiting-to-load');
            elem.classList.add('is-loaded');
        }

        _swapLoadingClassesOnLoad(event, picture, listener) {
            event.target.removeEventListener('load', listener, false);
            this._swapLoadingClasses(picture);
        }

        onFirstIntersection() {
            if (this.imageSetCss) {
                this.setAttribute('style', `background-image: image-set(${this.imageSetCss});`);
                this._swapLoadingClasses(this);
                return;
            }

            const picture = this.querySelector('picture');
            const image = this.querySelector('img');
            if (!picture || !image) {
                return;
            }

            const self = this;
            const listener = function (event) {
                self._swapLoadingClassesOnLoad(event, picture, listener);
            };

            if (window.HTMLPictureElement) {
                image.addEventListener('load', listener, false);

                const srcsetAttr = 'data-srcset';
                // move the 'data-srcset' attribute value to 'srcset'
                this.querySelectorAll('source').forEach((source) => {
                    const srcset = source.getAttribute(srcsetAttr);
                    if (srcset) {
                        source.setAttribute('srcset', srcset);
                        source.removeAttribute(srcsetAttr);
                    }
                });
            } else {
                // for browsers like IE that do not support the picture element,
                // we need to put the original resolution image stored in data-orig-src
                // as the img src attribute, replacing the "share" image which is
                // lower resolution.

                // move the 'data-src' attribute value to 'src'
                const srcAttr = 'data-src';
                const origSrc = image.getAttribute(srcAttr);
                const src = image.getAttribute('src');
                if (origSrc && origSrc !== src) {
                    image.addEventListener('load', listener, false);
                    image.setAttribute('src', origSrc);
                } else {
                    this._swapLoadingClasses(picture);
                }
                image.removeAttribute(srcAttr);
            }
        }
    }
    window.customElements.define('phoenix-picture', PhoenixPicture);
    measure.end();
});
